import { Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, afterNextRender, inject, numberAttribute } from '@angular/core';
import { ScrollTrigger, gsap } from '../gsap';
import { QUERIES } from '../motion-env';
import { MotionService } from '../motion.service';

/** Each strip holds 0-9 twice, so every column (zeros too) turns at least once before it settles. */
const CELLS = 20;
/** A number with its separators, e.g. `60,000`. Kept on one line. */
const NUMBER_RUN = /\d(?:[\d.,  ]*\d)?/g;

/**
 * Rolls the digits of a price like a car's odometer, once, when it scrolls into view. The
 * directive owns the element's text: everything that is not a digit (words, separators, the
 * currency) stays static, and the final text is exactly `value`. Screen readers get `value`
 * from a visually hidden copy while the digits roll; afterwards the element holds plain text.
 * Reduced motion, a value without digits, or a new value after the first roll: plain text.
 */
@Directive({ selector: '[appOdometer]' })
export class OdometerDirective implements OnChanges, OnDestroy {
  @Input('appOdometer') value: string | null | undefined = '';
  /** s, before the first column starts. */
  @Input({ transform: numberAttribute }) odometerDelay = 0;

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private ready = false;
  private played = false;
  private destroyed = false;
  private renderToken = 0;
  private mm?: ReturnType<typeof gsap.matchMedia>;

  constructor() {
    this.el.classList.add('dv-odometer');
    afterNextRender(() => {
      this.ready = true;
      this.scheduleRender();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['value']) return;
    // The new text shows right away; a roller (if any) is rebuilt by render().
    this.motion.outside(() => {
      this.mm?.revert();
      this.mm = undefined;
      this.writeText();
    });
    if (this.ready) this.scheduleRender();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
    this.mm = undefined;
  }

  /**
   * Building the rollers is DOM-heavy, so prices below the fold are built after the next paint
   * (the real text shows meanwhile). The latest value always wins.
   */
  private scheduleRender(): void {
    const token = ++this.renderToken;
    this.motion.whenNeeded(this.el, () => {
      if (!this.destroyed && token === this.renderToken) this.render();
    });
  }

  private writeText(): void {
    this.el.textContent = this.value ?? '';
  }

  private render(): void {
    this.mm?.revert();
    this.mm = undefined;
    this.writeText();
    const text = this.value ?? '';
    if (this.played || !/\d/.test(text)) return;

    const el = this.el;
    const mm = (this.mm = gsap.matchMedia(el));
    mm.add({ motion: QUERIES.motion }, (ctx, contextSafe) => {
      if (!ctx.conditions!['motion']) return;

      // Already scrolled past, or the page was rebuilt by a language switch: no replay.
      const rect = el.getBoundingClientRect();
      if (rect.bottom <= 0 || (this.motion.recentLanguageSwitch() && rect.top < window.innerHeight)) {
        this.played = true;
        return;
      }

      const { strips, digits } = this.buildRoller(text);
      const play = contextSafe!(() => {
        this.played = true;
        gsap.to(strips, {
          yPercent: (i: number) => (-(CELLS / 2 + digits[i]) * 100) / CELLS,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.06,
          delay: this.odometerDelay,
          // Hand back plain text: exact string, selectable, read once by screen readers.
          onComplete: () => this.writeText(),
        });
      }) as () => void;

      // Start as soon as the price shows, so the placeholder zeros are never left on screen.
      ScrollTrigger.create({ trigger: el, start: 'top bottom', once: true, onEnter: play });
      return () => this.writeText();
    });
  }

  /** `<span sr-only>value</span><span aria-hidden>text, .odo-col > .odo-strip per digit</span>` */
  private buildRoller(text: string): { strips: HTMLElement[]; digits: number[] } {
    const strips: HTMLElement[] = [];
    const digits: number[] = [];
    const make = (tag: string, className: string, content?: string): HTMLElement => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (content != null) node.textContent = content;
      return node;
    };

    const visual = make('span', 'odo');
    visual.setAttribute('aria-hidden', 'true');
    let last = 0;
    for (const match of text.matchAll(NUMBER_RUN)) {
      const start = match.index ?? 0;
      if (start > last) visual.append(text.slice(last, start));
      const run = make('span', 'odo-num');
      for (const ch of match[0]) {
        if (ch < '0' || ch > '9') {
          run.append(ch);
          continue;
        }
        const strip = make('span', 'odo-strip');
        for (let i = 0; i < CELLS; i++) strip.append(make('span', '', String(i % 10)));
        const col = make('span', 'odo-col');
        col.append(strip);
        run.append(col);
        strips.push(strip);
        digits.push(Number(ch));
      }
      visual.append(run);
      last = start + match[0].length;
    }
    if (last < text.length) visual.append(text.slice(last));

    this.el.replaceChildren(make('span', 'dv-sr-only', text), visual);
    return { strips, digits };
  }
}
